<template>
  <div>
    <b-card>
      <breadcrumbs
        :breadcrumb-items="breadcrumbItems"
      />
      <PartnerToolbar
        v-if="Object.keys(partner).length !== 0"
        :partner="partner"
      />
    </b-card>
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-top">
            <h4>{{ $t('Subscriptions') }}</h4>
          </div>
        </div>
      </div>
      <div
        v-if="loading"
        class="d-flex justify-content-center mb-1"
      >
        <b-spinner label="Loading..." />
      </div>
      <div v-else-if="subscribedList.length > 0">
        <b-table
          small
          :items="subscribedList"
          :fields="fields"
          responsive
          striped
          foot-clone
        >
          <template #head(name)>
            {{ $t('Partner') }}
          </template>

          <template #head(status)>
            {{ $t('message.tableHeader.status') }}
          </template>

          <template #cell(name)="row">
            <router-link
              :to="{ name: 'dashboard-dsppartner', params: { id: row.item._id } }"
              class="text-primary"
            >
              <strong>{{ row.item.name || row.item._id }}</strong>
            </router-link>
          </template>

          <template #cell(status)="row">
            <b-badge :variant="statusBadgeVariant(row.item.status)">
              {{ row.item.status || '—' }}
            </b-badge>
          </template>

          <template #foot(name)="data">
            <strong :data-label="data.label">{{ $t('Records') }}: {{ subscribedList.length }}</strong>
          </template>

          <template #foot(status)="data">
            <span :data-label="data.label" />
          </template>
        </b-table>
      </div>
      <div
        v-else
        class="alert-body text-center"
      >
        <span>{{ $t('No subscribers') }}</span>
      </div>
    </b-card>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import {
  BCard,
  BTable,
  BSpinner,
  BBadge,
} from 'bootstrap-vue'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import PartnerToolbar from '@/views/dashboard/partners/PartnerToolbar.vue'

const STATUS_VARIANT_MAP = {
  active: 'success',
  inactive: 'secondary',
  pending: 'warning',
  blocked: 'danger',
  suspended: 'dark',
}

export default {
  name: 'StatsSubscribed',
  components: {
    Breadcrumbs,
    BCard,
    BTable,
    BSpinner,
    BBadge,
    PartnerToolbar,
  },
  data() {
    return {
      loading: true,
      breadcrumbItems: [],
      partner: {},
      subscribedList: [],
      fields: [
        { key: 'name', label: 'Name', tdClass: 'td-width' },
        { key: 'status', label: 'Status', tdClass: 'td-width' },
      ],
    }
  },
  created() {
    this.getPartnerData()
  },
  methods: {
    async getPartnerData() {
      try {
        const responseData = await useJwt.getPartner(this.$route.params.id)
        this.partner = responseData.data.partner || {}
        this.setBreadcrumbs()
        await this.fetchSubscribed()
      } catch (e) {
        this.partner = {}
        this.subscribedList = []
      } finally {
        this.loading = false
      }
    },
    async fetchSubscribed() {
      this.loading = true
      try {
        const res = await useJwt.getPartnerSubscribed(this.$route.params.id)
        const raw = res.data
        const list = Array.isArray(raw)
          ? raw
          : (raw?.dsppartners ?? raw?.items ?? [])
        // active first, then by status, then rest
        this.subscribedList = [...list].sort((a, b) => {
          const sa = String((a.status || '')).toLowerCase()
          const sb = String((b.status || '')).toLowerCase()
          if (sa === 'active' && sb !== 'active') return -1
          if (sa !== 'active' && sb === 'active') return 1
          return sa.localeCompare(sb)
        })
      } catch (e) {
        this.subscribedList = []
      } finally {
        this.loading = false
      }
    },
    setBreadcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('RTB partners'),
          to: '/partners/',
        },
        {
          text: this.partner.name,
          to: `/partners/${this.partner._id}/`,
        },
        {
          text: this.$t('Subscriptions'),
          active: true,
        },
      ]
    },
    statusBadgeVariant(status) {
      if (!status) return 'secondary'
      const key = String(status).toLowerCase()
      return STATUS_VARIANT_MAP[key] || 'primary'
    },
  },
}
</script>

<style scoped>
[dir] .table.b-table > tbody td.td-width {
  width: 11%;
}
</style>
